import { motion, useScroll, useTransform } from "framer-motion";
import { SweepLink } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1706630810491-b4bbaa8c45b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxsb25kb24lMjBtYXlmYWlyJTIwYXJjaGl0ZWN0dXJlJTIwdHdpbGlnaHR8ZW58MHx8fHwxNzkxODMzNjg5fDA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { label: "Target Mandates", value: "£50M+" },
  { label: "Geography", value: "UK & Europe" },
  { label: "Focus", value: "Advisory · Capital · Sport" },
  { label: "Character", value: "Independent & Advisory-Led" },
];

export const Hero = ({ active = true }) => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 120]);

  const reveal = (delay) => ({
    initial: { opacity: 0, y: 20 },
    animate: active ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section id="home" data-testid="hero-section" className="relative pt-40 lg:pt-48 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal(0.25)} className="mb-10 flex items-center gap-4">
          <span className="h-px w-14 bg-gradient-to-r from-[#1F1E1B]/60 to-transparent" />
          <span className="font-sans text-[11px] uppercase tracking-[0.34em] text-[#6E6A61]">
            PJG Capital Partners
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline-text"
          className="font-serif font-light tracking-[-0.03em] leading-[1.02] text-[#1F1E1B] text-5xl sm:text-7xl lg:text-[6.5rem] max-w-6xl"
        >
          {["Independent capital.", "Considered advice."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-2">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={active ? { y: "0%" } : {}}
                transition={{ duration: 1.0, delay: 0.35 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
              >
                {i === 1 ? (
                  <>
                    <span className="italic">Considered</span>
                    {" advice."}
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 grid lg:grid-cols-12 gap-10 items-end">
          <motion.p {...reveal(0.95)} className="lg:col-span-7 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-[#6E6A61]">
            PJG Capital Partners is an independent strategic advisory firm serving private families,
            institutional investors and asset managers across complex transactions, private capital and
            alternative assets.
          </motion.p>
          <motion.div {...reveal(1.1)} className="lg:col-span-5 flex lg:justify-end">
            <SweepLink href="#capabilities" testid="hero-cta-expertise">
              Our Expertise
            </SweepLink>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={active ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 1.1, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-7xl mx-auto px-6 lg:px-10 mt-16 lg:mt-20"
      >
        <div className="relative aspect-[21/9] overflow-hidden border border-[#E3E0D8]">
          <motion.img
            src={HERO_IMG}
            alt="Aerial view of the City of London at twilight"
            className="w-full h-full object-cover grayscale contrast-[1.05] brightness-[0.92]"
            style={{ y: bgY, scale: 1.15 }}
          />
          <span className="absolute top-4 left-4 w-6 h-6 border-t border-l border-[#F6F4F0]/70" aria-hidden="true" />
          <span className="absolute top-4 right-4 w-6 h-6 border-t border-r border-[#F6F4F0]/70" aria-hidden="true" />
          <span className="absolute bottom-4 left-4 w-6 h-6 border-b border-l border-[#F6F4F0]/70" aria-hidden="true" />
          <span className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-[#F6F4F0]/70" aria-hidden="true" />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={active ? { opacity: 1 } : {}}
        transition={{ duration: 1, delay: 1.4 }}
        className="mt-16 lg:mt-20 border-t border-[#E3E0D8]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`hero-stat-${i}`}
              className={`py-6 lg:py-8 pr-6 ${i !== 0 ? "lg:border-l lg:border-[#E3E0D8] lg:pl-8" : ""}`}
            >
              <p className="font-sans text-[10px] uppercase tracking-[0.26em] text-[#6E6A61]">{s.label}</p>
              <p className="mt-2 font-serif text-lg sm:text-xl text-[#1F1E1B]">{s.value}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
